import styled from 'styled-components';
import { useFormikContext } from 'formik';
import { FormControlLabel, RadioGroup, Radio } from '@material-ui/core';
import { TextField } from '@avangard/ui/core';

import { UpdateStudentOnlineApplicationFormValues } from '@modules/students/requests';
import { StudentOnlineApplicationBlock } from '@modules/students/moleculas';
import { getMedicalGroupLabel, getMedicalGroupPlaceholder } from '@modules/students/helpers';
import {
    StyledFormLabel,
    FormRow,
    Clarification,
    ClarificationTextField,
    StyledFormHelperText,
} from './styled';

const StudentOnlineApplicationMedicalStep = () => {
    const { values, errors, handleChange, handleBlur } =
        useFormikContext<UpdateStudentOnlineApplicationFormValues>();

    const isAdultForMedicalGroupLabel = values.isAdult || values.isFutureAdult;

    return (
        <>
            <StudentOnlineApplicationBlock title='Медицинские данные'>
                <Content>
                    <FormRow>
                        <TextField
                            fullWidth
                            required
                            id='medical.diets'
                            label={getMedicalGroupLabel('diets', isAdultForMedicalGroupLabel)}
                            placeholder='Перечислите продукты, например: рыба, яйца, цитрусовые, орехи'
                            value={values.medical.diets}
                            error={!!errors.medical?.diets}
                            helperText={errors.medical?.diets}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </FormRow>

                    <FormRow>
                        <StyledFormLabel required>
                            {getMedicalGroupLabel('proneToCold', isAdultForMedicalGroupLabel)}
                        </StyledFormLabel>

                        <RadioGroup
                            row
                            name='medical.proneToColdBool'
                            value={values.medical.proneToColdBool}
                            onChange={handleChange}
                        >
                            <FormControlLabel value='0' label='Нет' control={<Radio />} />

                            <FormControlLabel value='1' label='Да' control={<Radio />} />
                        </RadioGroup>

                        {values.medical.proneToColdBool === '1' ? (
                            <Clarification>
                                <ClarificationTextField
                                    id='medical.proneToCold'
                                    placeholder='Введите уточнение'
                                    value={values.medical.proneToCold}
                                    error={!!errors.medical?.proneToCold}
                                    helperText={errors.medical?.proneToCold}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </Clarification>
                        ) : null}
                    </FormRow>

                    <TextField
                        fullWidth
                        required
                        id='medical.fractures'
                        label={getMedicalGroupLabel('fractures', isAdultForMedicalGroupLabel)}
                        placeholder='Например: перелом левой руки, 2015 год'
                        value={values.medical.fractures}
                        error={!!errors.medical?.fractures}
                        helperText={errors.medical?.fractures}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='medical.headInjuries'
                        label={getMedicalGroupLabel('headInjuries', isAdultForMedicalGroupLabel)}
                        placeholder='Например: легкое сотрясение мозга после падения, 2015 год'
                        value={values.medical.headInjuries}
                        error={!!errors.medical?.headInjuries}
                        helperText={errors.medical?.headInjuries}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='medical.allergicDiseases'
                        label={getMedicalGroupLabel(
                            'allergicDiseases',
                            isAdultForMedicalGroupLabel,
                        )}
                        placeholder='Например: аллергия на пыльцу, кошек'
                        value={values.medical.allergicDiseases}
                        error={!!errors.medical?.allergicDiseases}
                        helperText={errors.medical?.allergicDiseases}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='medical.allergyMedications'
                        label={getMedicalGroupLabel(
                            'allergyMedications',
                            isAdultForMedicalGroupLabel,
                        )}
                        placeholder='Укажите название препарата (например: аспирин, пенициллин)'
                        value={values.medical.allergyMedications}
                        error={!!errors.medical?.allergyMedications}
                        helperText={errors.medical?.allergyMedications}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='medical.chronicDiseases'
                        label={getMedicalGroupLabel('chronicDiseases', isAdultForMedicalGroupLabel)}
                        placeholder='Укажите заболевание (например: сахарный диабет)'
                        value={values.medical.chronicDiseases}
                        error={!!errors.medical?.chronicDiseases}
                        helperText={errors.medical?.chronicDiseases}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='medical.medications'
                        label={getMedicalGroupLabel('medications', isAdultForMedicalGroupLabel)}
                        placeholder={getMedicalGroupPlaceholder(
                            'medications',
                            isAdultForMedicalGroupLabel,
                        )}
                        value={values.medical.medications}
                        error={!!errors.medical?.medications}
                        helperText={errors.medical?.medications}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='medical.heatTransfer'
                        label={getMedicalGroupLabel('heatTransfer', isAdultForMedicalGroupLabel)}
                        placeholder={getMedicalGroupPlaceholder(
                            'heatTransfer',
                            isAdultForMedicalGroupLabel,
                        )}
                        value={values.medical.heatTransfer}
                        error={!!errors.medical?.heatTransfer}
                        helperText={errors.medical?.heatTransfer}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='medical.insectBites'
                        label={getMedicalGroupLabel('insectBites', isAdultForMedicalGroupLabel)}
                        placeholder='Например: обычная реакция, легкий зуд'
                        value={values.medical.insectBites}
                        error={!!errors.medical?.insectBites}
                        helperText={errors.medical?.insectBites}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        id='medical.healthSpecials'
                        label={getMedicalGroupLabel('healthSpecials', isAdultForMedicalGroupLabel)}
                        placeholder='Например: бронхиальная астма (иногда нужен ингалятор)'
                        value={values.medical.healthSpecials}
                        error={!!errors.medical?.healthSpecials}
                        helperText={errors.medical?.healthSpecials}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <FormRow>
                        <StyledFormLabel required>
                            {getMedicalGroupLabel('motionSickness', isAdultForMedicalGroupLabel)}
                        </StyledFormLabel>

                        <RadioGroup
                            row
                            name='medical.motionSicknessBool'
                            value={values.medical.motionSicknessBool}
                            onChange={handleChange}
                        >
                            <FormControlLabel value='0' label='Нет' control={<Radio />} />
                            <FormControlLabel value='1' label='Да' control={<Radio />} />
                        </RadioGroup>

                        {values.medical.motionSicknessBool === '1' ? (
                            <Clarification>
                                <ClarificationTextField
                                    id='medical.motionSickness'
                                    placeholder='Введите уточнение'
                                    value={values.medical.motionSickness}
                                    error={!!errors.medical?.motionSickness}
                                    helperText={errors.medical?.motionSickness}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </Clarification>
                        ) : null}

                        {!!errors.medical?.motionSicknessBool ? (
                            <StyledFormHelperText error>
                                {errors.medical.motionSicknessBool}
                            </StyledFormHelperText>
                        ) : null}
                    </FormRow>
                </Content>
            </StudentOnlineApplicationBlock>

            <StudentOnlineApplicationBlock title='Физическое состояние'>
                <Content>
                    <FormRow>
                        <StyledFormLabel required>
                            {getMedicalGroupLabel('fatigue', isAdultForMedicalGroupLabel)}
                        </StyledFormLabel>

                        <RadioGroup
                            row
                            name='medical.fatigueBool'
                            value={values.medical.fatigueBool}
                            onChange={handleChange}
                        >
                            <FormControlLabel value='0' label='Нет' control={<Radio />} />
                            <FormControlLabel value='1' label='Да' control={<Radio />} />
                        </RadioGroup>

                        {values.medical.fatigueBool === '1' ? (
                            <Clarification>
                                <ClarificationTextField
                                    id='medical.fatigue'
                                    placeholder='Введите уточнение'
                                    value={values.medical.fatigue}
                                    error={!!errors.medical?.fatigue}
                                    helperText={errors.medical?.fatigue}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </Clarification>
                        ) : null}

                        {!!errors.medical?.fatigueBool ? (
                            <StyledFormHelperText error>
                                {errors.medical.fatigueBool}
                            </StyledFormHelperText>
                        ) : null}
                    </FormRow>

                    <FormRow>
                        <StyledFormLabel required>
                            {getMedicalGroupLabel('wearingGlasses', isAdultForMedicalGroupLabel)}
                        </StyledFormLabel>

                        <RadioGroup
                            row
                            name='medical.wearingGlassesBool'
                            value={values.medical.wearingGlassesBool}
                            onChange={handleChange}
                        >
                            <FormControlLabel value='0' label='Нет' control={<Radio />} />
                            <FormControlLabel value='1' label='Да' control={<Radio />} />
                        </RadioGroup>

                        {values.medical.wearingGlassesBool === '1' ? (
                            <Clarification>
                                <ClarificationTextField
                                    id='medical.wearingGlasses'
                                    placeholder='Введите уточнение'
                                    value={values.medical.wearingGlasses}
                                    error={!!errors.medical?.wearingGlasses}
                                    helperText={errors.medical?.wearingGlasses}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </Clarification>
                        ) : null}

                        {!!errors.medical?.wearingGlassesBool ? (
                            <StyledFormHelperText error>
                                {errors.medical.wearingGlassesBool}
                            </StyledFormHelperText>
                        ) : null}
                    </FormRow>

                    <FormRow>
                        <StyledFormLabel required>
                            {getMedicalGroupLabel('fearDark', isAdultForMedicalGroupLabel)}
                        </StyledFormLabel>

                        <RadioGroup
                            row
                            name='medical.fearDarkBool'
                            value={values.medical.fearDarkBool}
                            onChange={handleChange}
                        >
                            <FormControlLabel value='0' label='Нет' control={<Radio />} />
                            <FormControlLabel value='1' label='Да' control={<Radio />} />
                        </RadioGroup>

                        {values.medical.fearDarkBool === '1' ? (
                            <Clarification>
                                <ClarificationTextField
                                    id='medical.fearDark'
                                    placeholder='Введите уточнение'
                                    value={values.medical.fearDark}
                                    error={!!errors.medical?.fearDark}
                                    helperText={errors.medical?.fearDark}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </Clarification>
                        ) : null}

                        {!!errors.medical?.fearDarkBool ? (
                            <StyledFormHelperText error>
                                {errors.medical.fearDarkBool}
                            </StyledFormHelperText>
                        ) : null}
                    </FormRow>

                    <TextField
                        fullWidth
                        id='medical.otherSpecials'
                        label='Другие особенности'
                        placeholder='Например: Боязнь высоты'
                        value={values.medical.otherSpecials}
                        error={!!errors.medical?.otherSpecials}
                        helperText={errors.medical?.otherSpecials}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                </Content>
            </StudentOnlineApplicationBlock>
        </>
    );
};

const Content = styled.div`
    & > *:not(:last-child) {
        margin-bottom: 24px;
    }
`;

export { StudentOnlineApplicationMedicalStep };
